import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Megaphone, Palette, Package, ShoppingBag, Monitor, Upload, ThumbsUp, ThumbsDown,
  Plus, Trash2, ExternalLink, CheckCircle2, X, Edit2, MessageSquare, Sparkles,
  Instagram, Twitter, ChevronDown, ChevronRight, Image as ImageIcon, FileText, Send, Star, BookOpen, FolderOpen,
  Clock, XCircle, AlertCircle
} from "lucide-react";
import { AssetCard, AssetDetailModal, ApprovalBadge, StatusBadge, APPROVAL_STATUSES } from "./marketing/components";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_TABS = [
  { key: "brand", label: "Brand", icon: Palette },
  { key: "packaging", label: "Packaging", icon: Package },
  { key: "merch", label: "Merch", icon: ShoppingBag },
  { key: "digital", label: "Digital", icon: Monitor },
];

const PACKAGING_STATUSES = [
  { value: "in_design", label: "In Design", color: "bg-amber-100 text-amber-700" },
  { value: "design_complete", label: "Design Complete", color: "bg-blue-100 text-blue-700" },
  { value: "samples_ordered", label: "Samples Ordered", color: "bg-purple-100 text-purple-700" },
  { value: "active", label: "Active", color: "bg-emerald-100 text-emerald-700" },
];

const DIGITAL_STATUSES = [
  { value: "ready_for_posting", label: "Ready for Posting", color: "bg-amber-100 text-amber-700" },
  { value: "posted", label: "Posted", color: "bg-emerald-100 text-emerald-700" },
];

const SUPPLIER_STATUSES = [
  { value: "option", label: "Option", color: "bg-stone-100 text-stone-700" },
  { value: "selected", label: "Selected", color: "bg-emerald-100 text-emerald-700" },
  { value: "rejected", label: "Rejected", color: "bg-red-100 text-red-700" },
];

// --- Supplier Card ---
function SupplierCard({ supplier, assets, onUpdate, onDelete }) {
  const taggedAssets = assets.filter(a => supplier.tagged_asset_ids?.includes(a.id));

  const changeStatus = async (newStatus) => {
    try {
      const res = await axios.put(`${API}/marketing/suppliers/${supplier.id}`, { status: newStatus });
      onUpdate(res.data);
      toast.success("Supplier status updated");
    } catch { toast.error("Failed"); }
  };

  return (
    <Card data-testid={`supplier-${supplier.id}`} className="bg-white border border-stone-200/60 rounded-xl">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h4 className="text-sm font-semibold text-stone-900">{supplier.name}</h4>
            {supplier.contact && <p className="text-xs text-stone-500">{supplier.contact}</p>}
          </div>
          <div className="flex items-center gap-1.5">
            <StatusBadge status={supplier.status} statuses={SUPPLIER_STATUSES} />
            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-stone-400 hover:text-red-500" onClick={() => onDelete(supplier.id)}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        {supplier.website && (
          <a href={supplier.website} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1 mb-2">
            <ExternalLink className="w-3 h-3" /> Website
          </a>
        )}
        {supplier.pricing_notes && <p className="text-xs text-stone-600 mb-2 bg-stone-50 rounded p-2">{supplier.pricing_notes}</p>}
        {taggedAssets.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {taggedAssets.map(a => <span key={a.id} className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded-full">{a.name}</span>)}
          </div>
        )}
        <div className="flex gap-1.5 pt-2 border-t border-stone-100">
          {SUPPLIER_STATUSES.map(s => (
            <Button key={s.value} size="sm" variant={supplier.status === s.value ? "default" : "outline"} className="text-xs h-6 px-2" onClick={() => changeStatus(s.value)}>
              {s.label}
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// --- AI Post Generator ---
function AIPostGenerator({ onSave }) {
  const [platform, setPlatform] = useState("instagram");
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("professional");
  const [includeImage, setIncludeImage] = useState(false);
  const [additionalContext, setAdditionalContext] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const [regeneratingImage, setRegeneratingImage] = useState(false);

  const generate = async () => {
    if (!topic.trim()) { toast.error("Enter a topic"); return; }
    setGenerating(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/marketing/generate-post`, { platform, topic, tone, include_image: includeImage, additional_context: additionalContext });
      setResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  const regenerateImage = async () => {
    if (!result) return;
    setRegeneratingImage(true);
    try {
      const res = await axios.post(`${API}/marketing/generate-post`, { platform, topic, tone, include_image: true, additional_context: additionalContext });
      setResult(prev => ({ ...prev, image_base64: res.data.image_base64 }));
      toast.success("New image generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Regeneration failed");
    } finally { setRegeneratingImage(false); }
  };

  const saveImage = async () => {
    if (!result?.image_base64) return;
    try {
      await onSave(result.image_base64, `${platform} post - ${topic.slice(0, 30)}`);
      toast.success("Saved to Digital assets");
    } catch { toast.error("Failed to save"); }
  };

  const platformIcons = { instagram: Instagram, twitter: Twitter, tiktok: Monitor };

  return (
    <div className="space-y-4" data-testid="ai-post-generator">
      <h3 className="text-lg font-semibold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>AI Post Generator</h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Platform */}
        <div>
          <label className="text-xs font-medium text-stone-500 mb-1 block">Platform</label>
          <div className="flex gap-1.5">
            {["instagram", "twitter", "tiktok"].map(p => {
              const Icon = platformIcons[p];
              return (
                <Button key={p} size="sm" variant={platform === p ? "default" : "outline"} onClick={() => setPlatform(p)} className="flex-1 text-xs capitalize">
                  <Icon className="w-3.5 h-3.5 mr-1" /> {p}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Tone */}
        <div>
          <label className="text-xs font-medium text-stone-500 mb-1 block">Tone</label>
          <select value={tone} onChange={e => setTone(e.target.value)} className="w-full h-9 text-sm border rounded-lg px-2 bg-white">
            <option value="professional">Professional</option>
            <option value="casual">Casual</option>
            <option value="playful">Playful</option>
            <option value="bold">Bold</option>
            <option value="inspirational">Inspirational</option>
          </select>
        </div>

        {/* Include Image */}
        <div>
          <label className="text-xs font-medium text-stone-500 mb-1 block">Include Image</label>
          <Button size="sm" variant={includeImage ? "default" : "outline"} onClick={() => setIncludeImage(!includeImage)} className="w-full text-xs">
            <ImageIcon className="w-3.5 h-3.5 mr-1" /> {includeImage ? "Image + Text" : "Text Only"}
          </Button>
        </div>
      </div>

      <div>
        <label className="text-xs font-medium text-stone-500 mb-1 block">Topic / Prompt</label>
        <textarea className="w-full text-sm border rounded-lg p-2.5 min-h-[80px]" value={topic} onChange={e => setTopic(e.target.value)} placeholder="e.g. New seasonal latte launch, behind-the-scenes brewing process..." data-testid="ai-post-topic" />
      </div>

      <div>
        <label className="text-xs font-medium text-stone-500 mb-1 block">Additional Context (optional)</label>
        <Input value={additionalContext} onChange={e => setAdditionalContext(e.target.value)} placeholder="Brand voice, specific hashtags, mentions..." className="text-sm" />
      </div>

      <Button onClick={generate} disabled={generating} className="bg-stone-900 text-white hover:bg-stone-800" data-testid="generate-post-btn">
        <Sparkles className="w-4 h-4 mr-2" /> {generating ? "Generating..." : "Generate Post"}
      </Button>

      {/* Result */}
      {result && (
        <Card className="bg-white border border-stone-200/60 rounded-xl">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-medium text-stone-500 uppercase">{result.platform}</span>
              {result.image_base64 && <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">With Image</span>}
            </div>
            {result.image_base64 && (
              <div className="mb-3 rounded-lg overflow-hidden relative">
                <img src={`data:image/png;base64,${result.image_base64}`} alt="Generated" className={`w-full ${regeneratingImage ? "opacity-30" : ""}`} />
                {regeneratingImage && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <p className="text-sm font-medium text-stone-600 bg-white/80 rounded-lg px-4 py-2">Generating new image...</p>
                  </div>
                )}
              </div>
            )}
            <div className="bg-stone-50 rounded-lg p-3 mb-3">
              <p className="text-sm text-stone-800 whitespace-pre-wrap">{result.text}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(result.text); toast.success("Copied!"); }}>
                Copy Text
              </Button>
              {result.image_base64 && (
                <>
                  <Button size="sm" variant="outline" onClick={regenerateImage} disabled={regeneratingImage}>
                    <Sparkles className="w-3.5 h-3.5 mr-1" /> {regeneratingImage ? "Regenerating..." : "Regenerate Image"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={saveImage}>
                    <Star className="w-3.5 h-3.5 mr-1" /> Save Image to Assets
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// --- Main Marketing Page ---
export default function Marketing() {
  const [activeTab, setActiveTab] = useState("brand");
  const [assets, setAssets] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [editingAsset, setEditingAsset] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [viewMode, setViewMode] = useState("all"); // "all" | "finalized"
  const [brandSubTab, setBrandSubTab] = useState("assets"); // "assets" | "finalized" | "playbook"
  const [collapsedCats, setCollapsedCats] = useState({});
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Upload form
  const [uploadName, setUploadName] = useState("");
  const [uploadDesc, setUploadDesc] = useState("");
  const [uploadTags, setUploadTags] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCategoryId, setUploadCategoryId] = useState("");

  // Category form
  const [newCatName, setNewCatName] = useState("");

  // Supplier form
  const [supplierName, setSupplierName] = useState("");
  const [supplierContact, setSupplierContact] = useState("");
  const [supplierWebsite, setSupplierWebsite] = useState("");
  const [supplierPricing, setSupplierPricing] = useState("");
  const [supplierTaggedIds, setSupplierTaggedIds] = useState([]);

  // Fetch user info to check if owner
  useEffect(() => {
    axios.get(`${API}/auth/me`).then(res => { setIsOwner(res.data.is_owner); setCurrentUserId(res.data.id); }).catch(() => {});
  }, []);

  // Fetch pending approvals for owners
  const fetchPendingApprovals = useCallback(async () => {
    if (isOwner) {
      try {
        const res = await axios.get(`${API}/marketing/pending-approvals`);
        setPendingApprovals(res.data);
      } catch { console.error("Failed to fetch pending approvals"); }
    }
  }, [isOwner]);

  const fetchAssets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/marketing/assets`, { params: { section: activeTab } });
      setAssets(res.data);
    } catch { console.error("Failed to fetch assets"); }
  }, [activeTab]);

  const fetchSuppliers = useCallback(async () => {
    if (activeTab === "packaging" || activeTab === "merch") {
      try {
        const res = await axios.get(`${API}/marketing/suppliers`, { params: { section: activeTab } });
        setSuppliers(res.data);
      } catch { console.error("Failed to fetch suppliers"); }
    }
  }, [activeTab]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/marketing/categories`, { params: { section: activeTab } });
      setCategories(res.data);
    } catch { console.error("Failed to fetch categories"); }
  }, [activeTab]);

  useEffect(() => {
    setLoading(true);
    setViewMode("all");
    setBrandSubTab("assets");
    setCollapsedCats({});
    Promise.all([fetchAssets(), fetchSuppliers(), fetchCategories(), fetchPendingApprovals()]).finally(() => setLoading(false));
  }, [activeTab, fetchAssets, fetchSuppliers, fetchCategories, fetchPendingApprovals]);

  const addCategory = async () => {
    if (!newCatName.trim()) { toast.error("Name required"); return; }
    try {
      await axios.post(`${API}/marketing/categories`, { section: activeTab, name: newCatName });
      toast.success("Category created");
      setNewCatName("");
      setShowAddCategory(false);
      fetchCategories();
    } catch { toast.error("Failed"); }
  };

  const deleteCategory = async (catId) => {
    if (!window.confirm("Delete this category? Assets will be uncategorized.")) return;
    try {
      await axios.delete(`${API}/marketing/categories/${catId}`);
      toast.success("Category deleted");
      fetchCategories();
      fetchAssets();
    } catch { toast.error("Failed"); }
  };

  const renameCategory = async (catId, newName) => {
    try {
      await axios.put(`${API}/marketing/categories/${catId}`, { name: newName });
      fetchCategories();
    } catch { toast.error("Failed"); }
  };

  const toggleCatCollapse = (catId) => setCollapsedCats(prev => ({ ...prev, [catId]: !prev[catId] }));

  const uploadAsset = async () => {
    if (!uploadFile || !uploadName.trim()) { toast.error("Name and file required"); return; }
    const fd = new FormData();
    fd.append("section", activeTab);
    fd.append("name", uploadName);
    fd.append("description", uploadDesc);
    fd.append("tags", uploadTags);
    fd.append("category_id", uploadCategoryId);
    fd.append("file", uploadFile);
    try {
      await axios.post(`${API}/marketing/assets`, fd);
      toast.success("Asset uploaded");
      setShowUpload(false);
      setUploadName(""); setUploadDesc(""); setUploadTags(""); setUploadFile(null); setUploadCategoryId("");
      fetchAssets();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  const deleteAsset = async (id) => {
    if (!window.confirm("Delete this asset?")) return;
    try {
      await axios.delete(`${API}/marketing/assets/${id}`);
      toast.success("Deleted");
      setSelectedAsset(null);
      fetchAssets();
    } catch { toast.error("Failed"); }
  };

  const addSupplier = async () => {
    if (!supplierName.trim()) { toast.error("Name required"); return; }
    try {
      await axios.post(`${API}/marketing/suppliers`, {
        section: activeTab, name: supplierName, contact: supplierContact,
        website: supplierWebsite, pricing_notes: supplierPricing, tagged_asset_ids: supplierTaggedIds,
      });
      toast.success("Supplier added");
      setShowAddSupplier(false);
      setSupplierName(""); setSupplierContact(""); setSupplierWebsite(""); setSupplierPricing(""); setSupplierTaggedIds([]);
      fetchSuppliers();
    } catch { toast.error("Failed"); }
  };

  const deleteSupplier = async (id) => {
    if (!window.confirm("Delete supplier?")) return;
    try {
      await axios.delete(`${API}/marketing/suppliers/${id}`);
      toast.success("Deleted");
      fetchSuppliers();
    } catch { toast.error("Failed"); }
  };

  const saveGeneratedImage = async (imageB64, name) => {
    await axios.post(`${API}/marketing/save-generated-image`, { image_base64: imageB64, name, section: "digital" });
    fetchAssets();
  };

  const getStatuses = () => {
    if (activeTab === "packaging" || activeTab === "merch") return PACKAGING_STATUSES;
    if (activeTab === "digital") return DIGITAL_STATUSES;
    return [];
  };

  const filteredAssets = viewMode === "finalized" ? assets.filter(a => a.is_finalized) : assets.filter(a => !a.is_finalized);
  const finalizedCount = assets.filter(a => a.is_finalized).length;

  // Group assets by category
  const renderCategoryGrid = (assetList, statuses, emptyIcon, emptyText) => {
    const uncategorized = assetList.filter(a => !a.category_id);
    const catGroups = categories.map(cat => ({
      ...cat,
      assets: assetList.filter(a => a.category_id === cat.id),
    }));

    if (categories.length === 0 && assetList.length === 0) {
      return (
        <div className="text-center py-12 text-stone-400">
          {emptyIcon}
          <p className="text-sm mt-2">{emptyText}</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Category groups */}
        {catGroups.map(cat => (
          <div key={cat.id} className="border border-stone-200/60 rounded-xl bg-white overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-stone-50/80 border-b border-stone-100 cursor-pointer" onClick={() => toggleCatCollapse(cat.id)}>
              {collapsedCats[cat.id] ? <ChevronRight className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
              <FolderOpen className="w-4 h-4 text-stone-500" />
              <span className="text-sm font-semibold text-stone-800">{cat.name}</span>
              <span className="text-xs text-stone-400">({cat.assets.length})</span>
              <button onClick={(e) => { e.stopPropagation(); const n = prompt("Rename category:", cat.name); if (n && n.trim()) renameCategory(cat.id, n.trim()); }} className="ml-auto text-stone-400 hover:text-stone-600"><Edit2 className="w-3.5 h-3.5" /></button>
              <button onClick={(e) => { e.stopPropagation(); deleteCategory(cat.id); }} className="text-stone-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            {!collapsedCats[cat.id] && (
              <div className="p-4">
                {cat.assets.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {cat.assets.map(a => (
                      <AssetCard key={a.id} asset={a} statuses={statuses} onSelect={setSelectedAsset} onEdit={setSelectedAsset} onDelete={deleteAsset} />
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-stone-400 text-center py-4">No assets in this category yet</p>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Uncategorized */}
        {uncategorized.length > 0 && (
          <div>
            {categories.length > 0 && <h4 className="text-xs font-medium text-stone-400 uppercase tracking-wider mb-3">Uncategorized</h4>}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {uncategorized.map(a => (
                <AssetCard key={a.id} asset={a} statuses={statuses} onSelect={setSelectedAsset} onDelete={deleteAsset} />
              ))}
            </div>
          </div>
        )}

        {categories.length > 0 && uncategorized.length === 0 && catGroups.every(c => c.assets.length === 0) && (
          <div className="text-center py-8 text-stone-400">
            <p className="text-sm">{emptyText}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-stone-50 p-4 sm:p-6 md:p-8" data-testid="marketing-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 text-stone-400 text-xs font-medium tracking-wider uppercase mb-2">
          <Megaphone className="w-4 h-4" /> Marketing
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Marketing</h1>
        <p className="text-sm text-stone-500 mb-6">Brand assets, packaging, merchandise, and digital content.</p>

        {/* Section Tabs */}
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 mb-6">
          <div className="flex gap-1 border-b border-stone-200 min-w-max">
            {SECTION_TABS.map(tab => {
              const Icon = tab.icon;
              const count = assets.length;
              return (
                <button
                  key={tab.key}
                  data-testid={`tab-${tab.key}`}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.key ? "border-stone-900 text-stone-900" : "border-transparent text-stone-500 hover:text-stone-700"
                  }`}
                >
                  <Icon className="w-4 h-4" /> {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Pending Approvals Banner (Owner Only) */}
        {isOwner && pendingApprovals.length > 0 && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-full">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-amber-900">{pendingApprovals.length} Asset{pendingApprovals.length > 1 ? 's' : ''} Pending Approval</h3>
                <p className="text-xs text-amber-700">Review and approve or reject submitted assets</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {pendingApprovals.slice(0, 3).map(a => (
                  <button key={a.id} onClick={() => setSelectedAsset(a)} className="px-3 py-1.5 text-xs font-medium bg-white border border-amber-300 rounded-full hover:bg-amber-100 transition-colors">
                    {a.name}
                  </button>
                ))}
                {pendingApprovals.length > 3 && (
                  <span className="px-3 py-1.5 text-xs text-amber-600">+{pendingApprovals.length - 3} more</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === "brand" && (
          <div>
            {/* Brand sub-tabs */}
            <div className="flex gap-4 mb-5">
              {[
                { key: "assets", label: "Designs" },
                { key: "finalized", label: `Finalized (${finalizedCount})` },
              ].map(st => (
                <button key={st.key} onClick={() => setBrandSubTab(st.key)}
                  className={`text-sm font-medium pb-1 ${brandSubTab === st.key ? "text-stone-900 border-b-2 border-stone-900" : "text-stone-400 hover:text-stone-600"}`}>
                  {st.label}
                </button>
              ))}
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowAddCategory(true)} data-testid="add-category-btn">
                  <FolderOpen className="w-3.5 h-3.5 mr-1" /> Add Category
                </Button>
                <Button size="sm" onClick={() => setShowUpload(true)} className="bg-stone-900 text-white hover:bg-stone-800" data-testid="upload-asset-btn">
                  <Upload className="w-3.5 h-3.5 mr-1" /> Upload
                </Button>
              </div>
            </div>

            {renderCategoryGrid(
              brandSubTab === "finalized" ? assets.filter(a => a.is_finalized) : assets.filter(a => !a.is_finalized),
              [],
              <Palette className="w-10 h-10 mx-auto opacity-30" />,
              brandSubTab === "finalized" ? "No finalized assets yet" : "Upload brand designs to get started"
            )}
          </div>
        )}

        {(activeTab === "packaging" || activeTab === "merch") && (
          <div>
            {/* View toggles */}
            <div className="flex items-center gap-3 mb-5">
              <div className="flex gap-1.5">
                {["all", "finalized"].map(m => (
                  <button key={m} onClick={() => setViewMode(m)}
                    className={`text-sm font-medium px-3 py-1 rounded-full ${viewMode === m ? "bg-stone-900 text-white" : "text-stone-500 hover:bg-stone-100"}`}>
                    {m === "all" ? "All" : `Finalized (${finalizedCount})`}
                  </button>
                ))}
              </div>
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowAddCategory(true)} data-testid="add-category-btn">
                  <FolderOpen className="w-3.5 h-3.5 mr-1" /> Add Category
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowAddSupplier(true)} data-testid="add-supplier-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Supplier
                </Button>
                <Button size="sm" onClick={() => setShowUpload(true)} className="bg-stone-900 text-white hover:bg-stone-800" data-testid="upload-asset-btn">
                  <Upload className="w-3.5 h-3.5 mr-1" /> Upload
                </Button>
              </div>
            </div>

            {/* Assets by Category */}
            <div className="mb-8">
              {renderCategoryGrid(
                filteredAssets,
                PACKAGING_STATUSES,
                <Package className="w-10 h-10 mx-auto opacity-30" />,
                viewMode === "finalized" ? "No finalized assets" : `Upload ${activeTab} designs to get started`
              )}
            </div>

            {/* Suppliers Section */}
            <div>
              <h3 className="text-lg font-semibold text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Suppliers</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {suppliers.map(s => (
                  <SupplierCard key={s.id} supplier={s} assets={assets} onUpdate={(updated) => setSuppliers(prev => prev.map(x => x.id === updated.id ? updated : x))} onDelete={deleteSupplier} />
                ))}
                {suppliers.length === 0 && (
                  <div className="col-span-full text-center py-8 text-stone-400">
                    <p className="text-sm">No suppliers added yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "digital" && (
          <div>
            {/* View toggles */}
            <div className="flex items-center gap-3 mb-5">
              <div className="flex gap-1.5">
                {["all", "finalized"].map(m => (
                  <button key={m} onClick={() => setViewMode(m)}
                    className={`text-sm font-medium px-3 py-1 rounded-full ${viewMode === m ? "bg-stone-900 text-white" : "text-stone-500 hover:bg-stone-100"}`}>
                    {m === "all" ? "All" : `Finalized (${finalizedCount})`}
                  </button>
                ))}
              </div>
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowAddCategory(true)} data-testid="add-category-btn">
                  <FolderOpen className="w-3.5 h-3.5 mr-1" /> Add Category
                </Button>
                <Button size="sm" onClick={() => setShowUpload(true)} className="bg-stone-900 text-white hover:bg-stone-800" data-testid="upload-asset-btn">
                  <Upload className="w-3.5 h-3.5 mr-1" /> Upload
                </Button>
              </div>
            </div>

            {/* Assets by Category */}
            <div className="mb-8">
              {renderCategoryGrid(
                filteredAssets,
                DIGITAL_STATUSES,
                <Monitor className="w-10 h-10 mx-auto opacity-30" />,
                viewMode === "finalized" ? "No finalized assets" : "Upload digital content to get started"
              )}
            </div>

            {/* AI Post Generator */}
            <div className="border-t pt-6">
              <AIPostGenerator onSave={saveGeneratedImage} />
            </div>
          </div>
        )}
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>Upload Asset</DialogTitle>
            <p className="text-sm text-stone-500">Add a new {activeTab} asset</p>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Name *" value={uploadName} onChange={e => setUploadName(e.target.value)} data-testid="upload-name" />
            <Input placeholder="Description" value={uploadDesc} onChange={e => setUploadDesc(e.target.value)} />
            <Input placeholder="Tags (comma-separated)" value={uploadTags} onChange={e => setUploadTags(e.target.value)} />
            {categories.length > 0 && (
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Category</label>
                <select value={uploadCategoryId} onChange={e => setUploadCategoryId(e.target.value)} className="w-full h-9 text-sm border rounded-lg px-2 bg-white">
                  <option value="">Uncategorized</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div className="border-2 border-dashed border-stone-200 rounded-lg p-4 text-center">
              <input type="file" onChange={e => setUploadFile(e.target.files?.[0] || null)} className="text-sm" accept="image/*,video/*,.pdf,.ai,.psd,.svg" data-testid="upload-file" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowUpload(false)} className="flex-1">Cancel</Button>
              <Button onClick={uploadAsset} className="flex-1 bg-stone-900 text-white hover:bg-stone-800" data-testid="upload-submit">Upload</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Supplier Dialog */}
      <Dialog open={showAddSupplier} onOpenChange={setShowAddSupplier}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>Add Supplier</DialogTitle>
            <p className="text-sm text-stone-500">Add a {activeTab} supplier</p>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Supplier name *" value={supplierName} onChange={e => setSupplierName(e.target.value)} data-testid="supplier-name" />
            <Input placeholder="Contact info" value={supplierContact} onChange={e => setSupplierContact(e.target.value)} />
            <Input placeholder="Website URL" value={supplierWebsite} onChange={e => setSupplierWebsite(e.target.value)} />
            <textarea className="w-full text-sm border rounded-lg p-2 min-h-[60px]" placeholder="Pricing notes" value={supplierPricing} onChange={e => setSupplierPricing(e.target.value)} />
            {assets.length > 0 && (
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Tag with items:</label>
                <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                  {assets.map(a => (
                    <button key={a.id} onClick={() => setSupplierTaggedIds(prev => prev.includes(a.id) ? prev.filter(x => x !== a.id) : [...prev, a.id])}
                      className={`text-xs px-2 py-1 rounded-full border ${supplierTaggedIds.includes(a.id) ? "bg-blue-50 border-blue-300 text-blue-700" : "border-stone-200 text-stone-500"}`}>
                      {a.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowAddSupplier(false)} className="flex-1">Cancel</Button>
              <Button onClick={addSupplier} className="flex-1 bg-stone-900 text-white hover:bg-stone-800" data-testid="supplier-submit">Add Supplier</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog open={showAddCategory} onOpenChange={setShowAddCategory}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>New Category</DialogTitle>
            <p className="text-sm text-stone-500">Create a category for {activeTab}</p>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Category name *" value={newCatName} onChange={e => setNewCatName(e.target.value)} autoFocus data-testid="category-name-input"
              onKeyDown={e => { if (e.key === "Enter") addCategory(); }} />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAddCategory(false)} className="flex-1">Cancel</Button>
              <Button onClick={addCategory} className="flex-1 bg-stone-900 text-white hover:bg-stone-800" data-testid="category-submit">Create</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Asset Detail Modal */}
      <AssetDetailModal
        asset={selectedAsset}
        open={!!selectedAsset}
        onClose={() => setSelectedAsset(null)}
        onUpdate={(updated) => { setSelectedAsset(updated); setAssets(prev => prev.map(a => a.id === updated.id ? updated : a)); fetchPendingApprovals(); }}
        onDelete={deleteAsset}
        onRefresh={fetchAssets}
        statuses={getStatuses()}
        section={activeTab}
        categories={categories}
        isOwner={isOwner}
        currentUserId={currentUserId}
      />
    </div>
  );
}
